import React, { useState, useEffect } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import useBanner from "../../../../hooks/useBanner";

export default function BannerModal({ setOpenBanner, onTrigger, banner }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    productType: "",
    title: "",
    description: "",
    location: "",
    link: "",
    image: null,
  });
  const [loadingToFetchLocation, setLoadingToFetchLocation] = useState(false);
  const [locationDatas, setLocationDatas] = useState([]);
  const { saveBanner, getLocations } = useBanner();

  useEffect(() => {
    const fetchData = async () => {
      setLoadingToFetchLocation(true);
      try {
        const response = await getLocations(setLoadingToFetchLocation);
        if (response) {
          setLocationDatas(response);
        }
      } catch (error) {
        setLoadingToFetchLocation(false);
      }
      setLoadingToFetchLocation(false);
    };

    fetchData();
  }, []);

  // Prefill if editing
  useEffect(() => {
    if (banner) {
      setFormData({
        productType: banner.product || "",
        title: banner.title || "",
        description: banner.description || "",
        location: banner.location || "",
        link: banner.link || "",
        image: banner.image || null,
      });
      setPreview(banner.image || null);
    }
  }, [banner]);

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.productType)
      newErrors.productType = "Product type is required";
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.location) newErrors.location = "Location is required";
    // if (!formData.link) {
    //   newErrors.link = "Link is required";
    // } 
    // else {
    //   const urlPattern =
    //     /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([\/\w .-]*)*\/?$/i;
    //   if (!urlPattern.test(formData.link)) {
    //     newErrors.link = "Link is not valid";
    //   }
    // }
    if (!formData.image) newErrors.image = "Image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, image: file }));
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, image: file }));
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const payload = new FormData();
    payload.append("product", formData.productType);
    payload.append("title", formData.title);
    payload.append("description", formData.description);
    payload.append("location", formData.location);
    payload.append("link", formData.link);
    if (formData.image && typeof formData.image !== "string") {
      payload.append("image", formData.image);
    }

    try {
      if (banner) {
        await saveBanner(setLoading, payload, banner._id, "PATCH");
      } else {
        await saveBanner(setLoading, payload);
      }
      void onTrigger();
      setOpenBanner(false);
    } catch (error) {
      console.error("Failed to submit banner:", error);
      setErrors({ submit: "Failed to save banner. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  function filterByProduct(data, product) {
    if (!Array.isArray(data)) {
      console.error("Expected an array of data");
      return [];
    }

    // Normalize product to lowercase to avoid case issues
    const filtered = data.filter(
      (item) => item.product?.toLowerCase() === product.toLowerCase()
    );

    return filtered;
  }

  const hotelData = filterByProduct(locationDatas, formData.productType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white w-full max-w-xl relative max-h-[94vh] overflow-y-auto rounded-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center bg-blue-600 text-white px-6 py-4 sticky top-0">
          <h2 className="text-lg font-semibold text-white">
            {banner ? "Update Banner" : "Add New Banner"}
          </h2>
          <CloseIcon
            onClick={() => setOpenBanner(false)}
            className="cursor-pointer"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 px-6 py-4">
          <div className="flex flex-col">
            <p className="text-sm">Select Product</p>
            <select
              name="productType"
              value={formData.productType}
              onChange={handleInputChange}
              className={`w-full border ${
                errors.productType ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            >
              <option value="">Select Product</option>
              <option value="flight">Flight</option>
              <option value="tour">Tour</option>
              <option value="hotel">Hotel</option>
            </select>
            {errors.productType && (
              <p className="text-red-500 text-xs mt-1">{errors.productType}</p>
            )}
          </div>

          {/* Title */}
          <div className="flex flex-col">
            <p className="text-sm">Banner Title</p>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter banner title"
              className={`w-full border ${
                errors.title ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            />
            {errors.title && (
              <p className="text-red-500 text-xs mt-1">{errors.title}</p>
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-sm">link</p>
            <input
              type="text"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              placeholder="Enter link"
              className={`w-full border ${
                errors.link ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            />
            {errors.link && (
              <p className="text-red-500 text-xs mt-1">{errors.link}</p>
            )}
          </div>

          <div className="flex flex-col">
            <p className="text-sm">Description</p>
            <textarea
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter short description"
              className={`w-full border ${
                errors.description ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">{errors.description}</p>
            )}
          </div>

          <div className="flex flex-col">
            <p className="text-sm">Select location</p>
            <select
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className={`w-full border ${
                errors.productType ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            >
              <option value="">Select location</option>

              {hotelData.map((item) => (
                <option key={item._id} value={item.location}>
                  {item.location.charAt(0).toUpperCase() +
                    item.location.slice(1)}
                </option>
              ))}
            </select>

            {errors.location && (
              <p className="text-red-500 text-xs mt-1">{errors.location}</p>
            )}
          </div>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className={`border ${
              errors.image ? "border-red-500" : "border-dashed border-gray-300"
            } rounded-lg flex flex-col items-center justify-center p-4 cursor-pointer hover:bg-gray-50`}
          >
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {preview ? (
              <div className="w-full flex items-center space-y-2">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-14 rounded-md object-cover"
                />
                <label
                  htmlFor="image-upload"
                  className="text-blue-600 hover:underline text-sm cursor-pointer ml-2"
                >
                  Change Image
                </label>
              </div>
            ) : (
              <label
                htmlFor="image-upload"
                className="flex flex-col items-center justify-center w-full h-full text-gray-400"
              >
                <CloudUploadIcon />
                <p className="text-sm">Drag & drop or click to upload image</p>
              </label>
            )}
            {errors.image && (
              <p className="text-red-500 text-xs mt-1">{errors.image}</p>
            )}
          </div>

          {errors.submit && (
            <p className="text-red-500 text-sm text-center">{errors.submit}</p>
          )}

          <div className="flex justify-between  mt-4">
            <button
              type="button"
              onClick={() => setOpenBanner(false)}
              className="bg-white border font-medium border-gray-100 rounded-md text-gray-700  py-2 px-3 flex justify-center items-center hover:bg-gray-100"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 rounded-md font-bold text-white py-2 px-3  flex justify-center items-center hover:bg-blue-700"
            >
              {loading
                ? banner
                  ? "Updating..."
                  : "Creating..."
                : banner
                ? "Update Banner"
                : "Create Banner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
